import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { goSearch } from './navigation';
import { useKeyboardMap, type KeyMap } from './useKeyboardMap';

/**
 * AppShell-level keyboard wiring. Owns the shortcuts that should fire
 * regardless of which screen is mounted (`/` for search, `?` for the
 * shortcut sheet). Returns the dialog-open state so the shell can
 * render the ShortcutSheet next to the Outlet.
 *
 * Screen-specific shortcuts (S1's 'A' for analyze, S5's '[' / ']' for
 * cursor stepping) live on the screens; they compose with this hook
 * because useKeyboardMap registers per-call. A future S5 hook can
 * call useKeyboardMap({ '[': … }) and both will fire.
 */
export function useGlobalShortcuts(): {
  shortcutSheetOpen: boolean;
  setShortcutSheetOpen: (open: boolean) => void;
} {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const map: KeyMap = useMemo(
    () => ({
      '/': {
        description: 'Open search',
        handler: () => goSearch(navigate),
      },
      '?': {
        description: 'Open the shortcut sheet',
        handler: () => setOpen(true),
      },
    }),
    [navigate],
  );

  useKeyboardMap(map);
  return { shortcutSheetOpen: open, setShortcutSheetOpen: setOpen };
}
